/**
 * Shared rules for criterion assessment UI across framework cycles,
 * custom cycles, and every viewport (desktop / tablet / phone).
 */

/** Treat "Not yet Assessed" and empty as unassessed; only real grade selections count as assessed. */
export function isGradeAssessed(grade: string | null | undefined): boolean {
  const g = (grade ?? "").trim();
  if (!g) return false;
  if (/^not\s+yet\s+assessed$/i.test(g)) return false;
  return true;
}

export function isNotApplicableGradeValue(grade: string | null | undefined): boolean {
  return (grade ?? "").trim().toLowerCase() === "not applicable";
}

/** Competent (or any non-NA) assessed grade requires an explicit assessment method. */
export function gradeRequiresAssessmentMethod(grade: string | null | undefined): boolean {
  return isGradeAssessed(grade) && !isNotApplicableGradeValue(grade);
}

export function hasAssessmentMethodSatisfied(
  grade: string | null | undefined,
  method: string | null | undefined
): boolean {
  if (!gradeRequiresAssessmentMethod(grade)) return true;
  return !!(method && String(method).trim());
}

/** Final assessment events must record a real outcome (not NA, not unassessed). */
export function isFinalEventAcceptableGrade(grade: string | null | undefined): boolean {
  return isGradeAssessed(grade) && !isNotApplicableGradeValue(grade);
}

/** Map stored grade to select value so empty / Not yet Assessed → "" (default option). */
export function gradeForAssessmentSelectDisplay(grade: string | null | undefined): string {
  const t = (grade ?? "").trim();
  if (!t || /^not\s+yet\s+assessed$/i.test(t)) return "";
  return t;
}

/**
 * Visual tone for assessed criterion tiles and grade pickers.
 * Pass / Competent* → green; Advisory → amber; Review / Not Safe / Fail → red; others uncoloured.
 */
export type CriterionGradeTileTone = "pass" | "advisory" | "fail" | "other" | null;

export function criterionGradeTileTone(grade: string | null | undefined): CriterionGradeTileTone {
  if (!isGradeAssessed(grade) || isNotApplicableGradeValue(grade)) return null;
  const g = (grade ?? "").trim().toLowerCase();
  if (g === "advisory" || /\badvisory\b/.test(g)) return "advisory";
  if (
    g === "review" ||
    /\breview\b/.test(g) ||
    g === "fail" ||
    /\bfail\b/.test(g) ||
    /\bnot\s+safe\b/.test(g) ||
    /\bunsafe\b/.test(g)
  ) {
    return "fail";
  }
  if (g === "pass" || (/\bcompetent\b/.test(g) && !/\bnot\s+yet\b/.test(g))) {
    return "pass";
  }
  return "other";
}

/** Pill classes for grade options / selected value (light + dark). Uncoloured tones stay neutral. */
export function gradeOptionPillClasses(grade: string | null | undefined, isDarkMode = false): string {
  const tone = criterionGradeTileTone(grade);
  if (tone === "pass") {
    return isDarkMode
      ? "bg-emerald-900/55 text-emerald-200 border border-emerald-600/70"
      : "bg-emerald-100 text-emerald-800 border border-emerald-200";
  }
  if (tone === "advisory") {
    return isDarkMode
      ? "bg-amber-900/55 text-amber-200 border border-amber-600/70"
      : "bg-amber-100 text-amber-800 border border-amber-200";
  }
  if (tone === "fail") {
    return isDarkMode
      ? "bg-rose-900/55 text-rose-200 border border-rose-600/70"
      : "bg-rose-100 text-rose-800 border border-rose-200";
  }
  return isDarkMode
    ? "bg-slate-800 text-slate-200 border border-slate-600"
    : "bg-slate-100 text-slate-700 border border-slate-200";
}

export type GuidedCriterionChipVisual = "complete" | "not_applicable" | "attention";

/**
 * Guided assessment navigator chips: green+tick when fully satisfied for this criterion,
 * grey+number when Not applicable, red+number when still needs grade/method/final-event rules.
 */
export function getGuidedCriterionChipVisual(
  grade: string | null | undefined,
  method: string | null | undefined,
  opts?: { isLastEvent?: boolean }
): GuidedCriterionChipVisual {
  const g = grade ?? "";
  if (!isGradeAssessed(g)) return "attention";
  if (isNotApplicableGradeValue(g)) return "not_applicable";
  if (!hasAssessmentMethodSatisfied(g, method)) return "attention";
  if (opts?.isLastEvent && !isFinalEventAcceptableGrade(g)) return "attention";
  return "complete";
}
